import {
  Fragment,
  MouseEvent,
  RefObject,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Comment, CommentAttachment } from '../../../services';
import { addComment, editComment, loadMoreComments } from '../../../slices';
import { COMMENTS_SIZE } from '../../constants';
import { useAppDispatch, useRefreshCommentsFeed } from '../../hooks';
import { getCssVar } from '../../utils';
import { IconLabel } from '../IconLabel';
import { CommentBox } from './CommentBox';
import { CommentItem } from './CommentItem';

import classes from './Comments.module.scss';

interface CommentsProps {
  storyId: number;
  cardId: number;
  comments: Comment[];
  commentsRootCount: number;
  loadMore?: () => void;
  wrapper?: HTMLElement;
}

export interface CommentPostArgs {
  content: string;
  attachments: CommentAttachment[];
}

interface CommentEditArgs extends CommentPostArgs {
  id: number;
}

interface RenderCommentItemArgs {
  comment: Comment;
  parentId?: number;
  replyId?: number;
}

interface RenderViewMoreRepliesArgs {
  id: number;
  viewMoreReplies: boolean;
  viewMoreRepliesCount: number;
}

export const Comments = memo(
  forwardRef<HTMLTextAreaElement, CommentsProps>(
    ({ storyId, cardId, comments, commentsRootCount, loadMore, wrapper }, commentBoxRef) => {
      const { t } = useTranslation();

      const dispatch = useAppDispatch();

      const { refreshCommentsFeed } = useRefreshCommentsFeed();

      const replyCommentBoxRef = useRef<HTMLTextAreaElement>(null);

      const editCommentBoxRef = useRef<HTMLTextAreaElement>(null);

      const [commentIdToReply, setCommentIdToReply] = useState<number>(0);

      const [commentIdToEdit, setCommentIdToEdit] = useState<number>(0);

      const [loading, setLoading] = useState(false);

      const resetCommentIdToReply = useCallback(() => {
        setCommentIdToReply(0);
      }, []);

      const resetCommentIdToEdit = useCallback(() => {
        setCommentIdToEdit(0);
      }, []);

      const loadMoreClickHandler = useCallback(
        async (event: MouseEvent<HTMLElement>, parentId?: number) => {
          try {
            setLoading(true);

            if (typeof loadMore === 'function') {
              loadMore();
              return;
            }

            const { top } = event.currentTarget.getBoundingClientRect();

            const loadedItems = parentId
              ? comments.find(({ id }) => id === parentId)?.replies ?? []
              : comments;

            await dispatch(
              loadMoreComments({
                parentId,
                storyId,
                itemId: cardId,
                size: COMMENTS_SIZE,
                page: Math.floor(loadedItems.length / COMMENTS_SIZE) + 1,
              })
            );

            (wrapper ?? window).scrollBy({ top: top - 80, behavior: 'smooth' });
          } catch (_) {
            toast.error(t('comments.load-more-error'));
          } finally {
            setLoading(false);
          }
        },
        [cardId, comments, dispatch, loadMore, storyId, t, wrapper]
      );

      const renderCommentItem = useCallback(
        ({ comment, parentId, replyId }: RenderCommentItemArgs) => {
          const onReply = () => {
            setCommentIdToReply(parentId ?? comment.id);
            resetCommentIdToEdit();
          };

          const onEdit = () => {
            setCommentIdToEdit(replyId ?? comment.id);
            resetCommentIdToReply();
          };

          return (
            <CommentItem
              comment={comment}
              onReply={onReply}
              onEdit={onEdit}
              parentId={parentId}
              isParentHidden={comments.find(({ id }) => id === parentId)?.isHidden ?? false}
            />
          );
        },
        [comments, resetCommentIdToEdit, resetCommentIdToReply]
      );

      const viewMoreComments = useMemo(
        () => commentsRootCount > comments.length,
        [comments.length, commentsRootCount]
      );

      const viewMoreCommentsCount = useMemo(
        () => Math.min(COMMENTS_SIZE, commentsRootCount - comments.length),
        [comments.length, commentsRootCount]
      );

      const renderViewMoreComments = useMemo(() => {
        if (!viewMoreComments) {
          return null;
        }

        return (
          <IconLabel
            className={classes['comments__view-more-comments']}
            label={t('comments.view-more', { count: viewMoreCommentsCount })}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
            onClick={loadMoreClickHandler}
            disabled={loading}
          />
        );
      }, [loadMoreClickHandler, loading, t, viewMoreComments, viewMoreCommentsCount]);

      const renderViewMoreReplies = useCallback(
        ({ id, viewMoreReplies, viewMoreRepliesCount }: RenderViewMoreRepliesArgs) => {
          if (!viewMoreReplies) {
            return null;
          }

          return (
            <IconLabel
              className={classes['comments__view-more-replies']}
              label={t('comments.view-more-replies', { count: viewMoreRepliesCount })}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
              onClick={(event) => loadMoreClickHandler(event, id)}
              disabled={loading}
            />
          );
        },
        [loadMoreClickHandler, loading, t]
      );

      const postCommentHandler = useCallback(
        (parentId?: number) => {
          return async ({ content, attachments }: CommentPostArgs) => {
            await dispatch(addComment({ storyId, itemId: cardId, content, attachments, parentId }));

            resetCommentIdToReply();

            refreshCommentsFeed();
          };
        },
        [cardId, dispatch, refreshCommentsFeed, resetCommentIdToReply, storyId]
      );

      const editCommentHandler = useCallback(
        (commentId: number) => {
          return async ({ content, attachments }: CommentPostArgs) => {
            await dispatch(editComment({ storyId, commentId, content, attachments }));
            resetCommentIdToEdit();
          };
        },
        [dispatch, resetCommentIdToEdit, storyId]
      );

      const renderCommentEdit = useCallback(
        ({ id, content, attachments }: CommentEditArgs) => {
          return (
            <CommentBox
              ref={editCommentBoxRef}
              content={content}
              attachments={attachments}
              onSubmit={() => editCommentHandler(id)}
              onClose={resetCommentIdToEdit}
            />
          );
        },
        [editCommentHandler, resetCommentIdToEdit]
      );

      const setInputFocus = (inputRef: RefObject<HTMLTextAreaElement>, id: number) => {
        if (!inputRef.current || !id) {
          return;
        }

        const input = inputRef.current;

        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      };

      useEffect(() => {
        setInputFocus(replyCommentBoxRef, commentIdToReply);
        setInputFocus(editCommentBoxRef, commentIdToEdit);
      }, [commentIdToEdit, commentIdToReply]);

      return (
        <div className={classes['comments']}>
          <CommentBox ref={commentBoxRef} onSubmit={postCommentHandler} />

          {Boolean(commentsRootCount > 0) && (
            <div className={classes['comments__list']}>
              {comments.map((comment) => {
                const { id, content, replies, repliesCount, attachments } = comment;

                return (
                  <div key={id} className={classes['comments__list-comment']}>
                    {renderCommentItem({ comment })}
                    {id === commentIdToEdit && renderCommentEdit({ id, content, attachments })}

                    {Boolean(replies.length) && (
                      <div className={classes['comments__list-reply']}>
                        {replies.map((reply) => {
                          const {
                            id: replyId,
                            content: replyContent,
                            attachments: replyAttachments,
                          } = reply;

                          return (
                            <Fragment key={replyId}>
                              {renderCommentItem({ comment: reply, parentId: id, replyId })}
                              {replyId === commentIdToEdit &&
                                renderCommentEdit({
                                  id: replyId,
                                  content: replyContent,
                                  attachments: replyAttachments,
                                })}
                            </Fragment>
                          );
                        })}
                      </div>
                    )}
                    {id === commentIdToReply && (
                      <div className={classes['comments__list-reply-active']}>
                        <CommentBox
                          ref={replyCommentBoxRef}
                          onSubmit={() => postCommentHandler(id)}
                          onClose={resetCommentIdToReply}
                        />
                      </div>
                    )}
                    {renderViewMoreReplies({
                      id,
                      viewMoreReplies: repliesCount > replies.length,
                      viewMoreRepliesCount: Math.min(COMMENTS_SIZE, repliesCount - replies.length),
                    })}
                  </div>
                );
              })}
              {renderViewMoreComments}
            </div>
          )}
        </div>
      );
    }
  )
);
